import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { RotateCcw, Eye, EyeOff, Lock, Plus, X, ChevronUp, ChevronDown } from 'lucide-react';
import { DEVIS_FORMS, DEVIS_UNIVERSES } from '../../lib/questionnaires';
import { fetchOverrides, hasRequired } from '../../lib/questionnaireOverrides';

const EMPTY = { hiddenSections: {}, labels: {}, options: {} };

const FormSettingsManager = () => {
  const [ov, setOv] = useState(null);
  const [tab, setTab] = useState('weddings');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchOverrides(true).then(setOv); }, []);

  if (!ov) return <p className="text-center text-[#6B6B6B] py-20">Chargement…</p>;

  const cur = { ...EMPTY, ...ov[tab] };
  const patch = (k, fn) => setOv((p) => ({ ...p, [tab]: { ...EMPTY, ...p[tab], [k]: fn({ ...EMPTY, ...p[tab] }[k]) } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/questionnaire-overrides', ov);
      setOv(data);
      fetchOverrides(true);
      toast.success('Questionnaire enregistré');
    } catch {
      toast.error("Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-formsettings-manager">
      <div className="mb-10">
        <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Personnalisation</span>
        <h2 className="text-4xl font-serif text-[#111111]">Questionnaires de devis</h2>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Reformulez les questions et choisissez les sections affichées sur la page /devis.
          Les sections contenant une information obligatoire restent toujours visibles.
        </p>
      </div>
      <div className="flex flex-wrap gap-2 mb-8" role="tablist">
        {DEVIS_UNIVERSES.map((u) => (
          <button key={u.key} role="tab" aria-selected={tab === u.key} onClick={() => setTab(u.key)} data-testid={`qtab-${u.key}`}
            className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border transition-colors ${tab === u.key ? 'bg-[#111111] text-white border-[#111111]' : 'border-[#111111]/20 text-[#111111] hover:border-[#111111]'}`}>
            {u.label}
          </button>
        ))}
      </div>
      <div className="space-y-6">
        {DEVIS_FORMS[tab].sections.map((s, si) => (
          <SectionEditor key={`${tab}-${si}`} s={s} id={`${tab}-${si}`} ov={cur}
            onHide={(h) => patch('hiddenSections', (m) => ({ ...m, [s.title]: h }))}
            onLabel={(orig, v) => patch('labels', (m) => ({ ...m, [orig]: v }))}
            onOptions={(orig, v) => patch('options', (m) => ({ ...m, [orig]: v }))} />
        ))}
      </div>
      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-[#111111]/10 py-4 mt-8 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="qsettings-save"
          className="px-10 py-4 bg-[#111111] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#333333] disabled:opacity-50 transition-colors">
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
};

const SectionEditor = ({ s, id, ov, onHide, onLabel, onOptions }) => {
  const locked = hasRequired(s);
  const hidden = !locked && (ov.hiddenSections[s.title] ?? !!s.hidden);
  return (
    <div className={`border border-[#111111]/10 bg-white ${hidden ? 'opacity-60' : ''}`} data-testid={`qsection-${id}`}>
      <div className="flex items-center justify-between gap-4 p-5 border-b border-[#111111]/10 bg-[#F5F5F5]/40">
        <h3 className="font-serif text-xl text-[#111111]">{s.title}</h3>
        {locked ? (
          <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-[#6B6B6B]"><Lock className="w-3.5 h-3.5" />Toujours affichée</span>
        ) : (
          <button onClick={() => onHide(!hidden)} data-testid={`qsection-toggle-${id}`}
            className="flex items-center gap-2 px-4 py-2 border border-[#111111]/20 text-[11px] uppercase tracking-[0.15em] hover:border-[#111111] transition-colors">
            {hidden ? <><EyeOff className="w-3.5 h-3.5" />Masquée</> : <><Eye className="w-3.5 h-3.5" />Affichée</>}
          </button>
        )}
      </div>
      <div className="p-5 space-y-3">
        {s.fields.map((f, fi) => (
          <div key={fi}>
            <LabelRow f={f} id={`${id}-${fi}`} value={ov.labels[f.label]} onChange={(v) => onLabel(f.label, v)} />
            {(f.type === 'radio' || f.type === 'checkbox') && (
              <OptionsEditor id={`${id}-${fi}`} defaults={f.options} list={ov.options[f.label]} onChange={(v) => onOptions(f.label, v)} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const KIND = { text: 'Texte', textarea: 'Texte long', radio: 'Choix unique', checkbox: 'Choix multiples', file: 'Fichier', note: 'Note', consent: 'Case à cocher', universe: 'Univers' };

const LabelRow = ({ f, id, value, onChange }) => {
  const changed = value != null && value !== '' && value !== f.label;
  return (
    <div className="flex items-start gap-3">
      <span className="w-28 shrink-0 pt-2.5 text-[10px] uppercase tracking-[0.12em] text-[#6B6B6B]">{KIND[f.type] || f.type}{f.required ? ' *' : ''}</span>
      <textarea rows={f.label.length > 90 ? 3 : 1} value={value ?? f.label} onChange={(e) => onChange(e.target.value)}
        className={`flex-1 px-3 py-2 text-sm border resize-y focus:outline-none focus:border-[#111111] transition-colors ${changed ? 'border-[#111111]/40 bg-white' : 'border-transparent bg-[#F5F5F5]/60'}`}
        data-testid={`qlabel-${id}`} />
      <button onClick={() => onChange(undefined)} disabled={!changed} title="Revenir au texte d'origine" data-testid={`qlabel-reset-${id}`}
        className="mt-1.5 w-8 h-8 flex items-center justify-center text-[#6B6B6B] hover:text-[#111111] disabled:opacity-20">
        <RotateCcw className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const btn = 'w-6 h-6 flex items-center justify-center text-[#6B6B6B] hover:text-[#111111] disabled:opacity-20';

const OptionsEditor = ({ id, defaults, list, onChange }) => {
  const [adding, setAdding] = useState('');
  const cur = list?.length ? list : defaults;
  const move = (i, d) => { const n = [...cur]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  const add = () => {
    const v = adding.trim();
    if (!v) return;
    if (cur.includes(v)) { toast.error('Déjà dans la liste'); return; }
    onChange([...cur, v]); setAdding('');
  };
  return (
    <div className="ml-[7.75rem] mr-11 mt-2 mb-4 border-l border-[#111111]/10 pl-4" data-testid={`qoptions-${id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B6B6B]">Choix proposés ({cur.length})</span>
        {!!list?.length && list.join('|') !== defaults.join('|') && (
          <button onClick={() => onChange(undefined)} className="text-[10px] uppercase tracking-[0.15em] text-[#6B6B6B] hover:text-[#111111] flex items-center gap-1" data-testid={`qoptions-reset-${id}`}>
            <RotateCcw className="w-3 h-3" />Choix d'origine
          </button>
        )}
      </div>
      <ul className="space-y-1">
        {cur.map((o, i) => (
          <li key={i} className="flex items-center gap-1">
            <input value={o} onChange={(e) => onChange(cur.map((x, j) => (j === i ? e.target.value : x)))}
              className="flex-1 px-2 py-1.5 text-sm bg-[#F5F5F5]/60 border border-transparent focus:bg-white focus:border-[#111111] focus:outline-none" data-testid={`qoption-${id}-${i}`} />
            <button onClick={() => move(i, -1)} disabled={i === 0} className={btn} aria-label="Monter"><ChevronUp className="w-3.5 h-3.5" /></button>
            <button onClick={() => move(i, 1)} disabled={i === cur.length - 1} className={btn} aria-label="Descendre"><ChevronDown className="w-3.5 h-3.5" /></button>
            <button onClick={() => onChange(cur.filter((_, j) => j !== i))} disabled={cur.length <= 1} className={`${btn} hover:text-red-600`} aria-label="Retirer" data-testid={`qoption-remove-${id}-${i}`}><X className="w-3.5 h-3.5" /></button>
          </li>
        ))}
      </ul>
      <div className="flex gap-1 mt-2">
        <input value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Nouveau choix…" className="flex-1 px-2 py-1.5 text-sm border border-[#111111]/20 focus:border-[#111111] focus:outline-none" data-testid={`qoption-add-input-${id}`} />
        <button onClick={add} disabled={!adding.trim()} className="px-3 bg-[#111111] text-white disabled:opacity-40" aria-label="Ajouter" data-testid={`qoption-add-btn-${id}`}><Plus className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
};

export default FormSettingsManager;
